import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    initialStep: { type: Number, default: 0 },
    hasOnboardingMaterials: { type: Boolean, default: false },
    hasRequiredAttestations: { type: Boolean, default: false },
    showEmptyMaterialSteps: { type: Boolean, default: false }
  }

  static targets = [
    "step",
    "stepNumber",
    "stepLabel",
    "stepName",
    "progressBar",
    "progressItem",
    "overviewItem",
    "backButton",
    "nextButton",
    "submitButton",
    "review",
    "sportsSearch",
    "sportsOption",
    "sportsEmpty",
    "dob",
    "concussionCount",
    "concussionDateField",
    "registrantKind",
    "personaPath",
    "personaPathBody",
    "personaPathStatus",
    "registrantDetails",
    "guardianDetails",
    "adultApprovalNotice",
    "authorizedContactToggle",
    "authorizedContactSection",
    "authorizedContactItem",
    "authorizedContactTypeToggle",
    "authorizedContactPanel",
    "authorizedContactDetails",
    "contactTypeOtherField",
    "informationSharingHelp",
    "reviewIntro",
    "guardianApprovalFields",
    "athleteEmailField",
    "athleteEmail",
    "athleteEmailOptionalMark",
    "athleteEmailHelp",
    "registrantFirstName",
    "registrantLastName",
    "registrantEmail",
    "registrantPhone",
    "registrantRelationship",
    "guardianSameAsRegistrant",
    "guardianManualDetails",
    "guardianFirstName",
    "guardianLastName",
    "guardianEmail",
    "guardianPhone",
    "guardianRelationship",
    "guardianSignature",
    "contactPhone",
    "contactPhoneHelp",
    "contactChannelNotice",
    "allowedCategory",
    "allowedChannel",
    "allInformation"
  ]

  connect() {
    this.currentStep = this.initialStepValue
    this.guardianSignatureWasEdited = false
    this.guardianSameAsRegistrantWasChanged = false
    this.stepNames = {
      0: "Basic information",
      1: "Sports",
      2: "Health history",
      3: "Access and communication",
      4: "Onboarding materials",
      5: "Attestations",
      6: "Overall review"
    }
    this.stepShortNames = {
      0: "Basic",
      1: "Sports",
      2: "Health",
      3: "Access",
      4: "Materials",
      5: "Attest",
      6: "Review"
    }
    this.initializeDependents()
    this.refreshRegistrationAccess()
    this.showStep(false)
  }

  next() {
    if (!this.validateStep(this.currentStep)) return

    const stepOrder = this.orderedStepIndexes()
    const currentPosition = this.currentStepPosition(stepOrder)

    if (currentPosition < stepOrder.length - 1) {
      this.currentStep = stepOrder[currentPosition + 1]
      this.showStep()
    }
  }

  previous() {
    const stepOrder = this.orderedStepIndexes()
    const currentPosition = this.currentStepPosition(stepOrder)

    if (currentPosition > 0) {
      this.currentStep = stepOrder[currentPosition - 1]
      this.showStep()
    }
  }

  validateSubmission(event) {
    const stepOrder = this.orderedStepIndexes()

    for (const index of stepOrder) {
      if (!this.validateStep(index)) {
        event.preventDefault()
        this.currentStep = index
        this.showStep()
        return
      }
    }
  }

  validateStep(index) {
    const controls = this.stepControls(index)
    const invalidControl = controls.find((control) => !control.checkValidity())

    if (!invalidControl) return true

    invalidControl.reportValidity()
    invalidControl.focus({ preventScroll: true })
    invalidControl.scrollIntoView({ behavior: "smooth", block: "center" })
    return false
  }

  showStep(shouldScroll = true) {
    const stepOrder = this.orderedStepIndexes()

    if (!stepOrder.includes(this.currentStep)) {
      this.currentStep = stepOrder[0]
    }

    const currentPosition = this.currentStepPosition(stepOrder)

    this.stepTargets.forEach((step, index) => {
      step.hidden = index !== this.currentStep
    })

    this.progressItemTargets.forEach((item, index) => {
      const orderedStepIndex = stepOrder[index]
      const label = item.querySelector("small")
      const number = item.querySelector("span")

      item.hidden = orderedStepIndex === undefined

      if (orderedStepIndex !== undefined) {
        if (number) number.textContent = index + 1
        if (label) label.textContent = this.stepShortNames[orderedStepIndex]
      }

      item.classList.toggle("is-current", index === currentPosition)
      item.classList.toggle("is-complete", index < currentPosition)
      if (index === currentPosition) {
        item.setAttribute("aria-current", "step")
      } else {
        item.removeAttribute("aria-current")
      }
    })

    this.overviewItemTargets.forEach((item, index) => {
      const stepIndex = Number(item.dataset.registrationStepIndex ?? index)
      const position = stepOrder.indexOf(stepIndex)
      const number = item.querySelector("span")

      item.hidden = position < 0
      if (number && position >= 0) number.textContent = position + 1
    })

    this.stepNumberTargets.forEach((target, index) => {
      const position = stepOrder.indexOf(index)
      target.textContent = position >= 0 ? `Step ${position + 1}` : ""
    })

    this.stepLabelTarget.textContent = `Step ${currentPosition + 1} of ${stepOrder.length}`
    this.stepNameTarget.textContent = this.stepNames[this.currentStep]
    this.progressBarTarget.style.width = `${((currentPosition + 1) / stepOrder.length) * 100}%`

    this.backButtonTarget.hidden = currentPosition === 0
    this.nextButtonTarget.hidden = currentPosition === stepOrder.length - 1
    this.submitButtonTarget.hidden = currentPosition !== stepOrder.length - 1

    if (currentPosition === stepOrder.length - 1) this.buildReview()

    this.refreshActionState()
    this.refreshSubmitLabel()

    if (shouldScroll) {
      this.element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  refreshActionState() {
    const readyToSubmit = this.orderedStepIndexes().every((index) => this.stepIsValid(index))

    this.setButtonState(this.nextButtonTarget, false)
    this.setButtonState(this.submitButtonTarget, !readyToSubmit)
  }

  refreshSubmitLabel() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.value = this.currentPathCopy().submitLabel
  }

  toggleDependent(event) {
    const dependentId = event.currentTarget.dataset.dependentId
    if (!dependentId) return

    this.updateDependent(dependentId, true)
  }

  initializeDependents() {
    const dependentIds = new Set(
      Array.from(this.element.querySelectorAll("[data-dependent-id]"))
        .map((control) => control.dataset.dependentId)
        .filter(Boolean)
    )

    dependentIds.forEach((dependentId) => this.updateDependent(dependentId, false))
  }

  refreshRegistrationAccess() {
    const subjectIsMinor = this.subjectAge() !== null && this.subjectAge() < 18
    const registrantKind = this.selectedRegistrantKind()
    const registrantIsSomeoneElse = registrantKind === "someone_else"
    const minorCompletingOwnRegistration = this.minorCompletingOwnRegistration()
    const informationSharingAvailable = !minorCompletingOwnRegistration
    const addingAuthorizedContact = informationSharingAvailable && this.selectedAuthorizedContactValue() === "1"
    const guardianCanSignNow = subjectIsMinor && registrantIsSomeoneElse

    this.updateAthleteEmailRequirement()
    this.togglePanel(this.registrantDetailsTarget, registrantIsSomeoneElse)
    this.togglePanel(this.guardianDetailsTarget, subjectIsMinor)
    this.togglePanel(this.guardianApprovalFieldsTarget, guardianCanSignNow)
    this.togglePanel(this.adultApprovalNoticeTarget, !subjectIsMinor && registrantIsSomeoneElse)
    this.togglePanel(this.authorizedContactSectionTarget, informationSharingAvailable)
    if (this.hasAuthorizedContactDetailsTarget) {
      this.togglePanel(this.authorizedContactDetailsTarget, addingAuthorizedContact)
    }
    this.updateAuthorizedContactCards(informationSharingAvailable)
    this.updatePersonaPath(subjectIsMinor, registrantKind)
    this.updatePathCopy(subjectIsMinor, registrantKind)
    this.updateContactChannelRequirements(addingAuthorizedContact)
    this.updateGuardianSameAsRegistrantState(subjectIsMinor && registrantIsSomeoneElse)
    this.syncGuardianFromRegistrant()
    this.syncGuardianSignature()
    this.refreshConcussionDate()

    this.refreshActionState()
  }

  refreshConcussionDate() {
    if (!this.hasConcussionCountTarget || !this.hasConcussionDateFieldTarget) return

    const count = Number.parseInt(this.concussionCountTarget.value, 10)
    const shouldShow = Number.isFinite(count) && count > 0 && !this.concussionCountTarget.disabled

    this.concussionDateFieldTarget.hidden = !shouldShow
    this.concussionDateFieldTarget.querySelectorAll("input, select, textarea").forEach((field) => {
      field.disabled = !shouldShow
      if (!shouldShow) field.value = ""
    })
  }

  updatePersonaPath(subjectIsMinor, registrantKind) {
    if (!this.hasPersonaPathTarget) return

    const age = this.subjectAge()
    const content = this.personaPathContent(age, subjectIsMinor, registrantKind)

    this.personaPathTarget.dataset.personaPath = content.key
    this.personaPathBodyTarget.textContent = content.body
    this.personaPathStatusTarget.textContent = content.status
  }

  updatePathCopy(subjectIsMinor, registrantKind) {
    const content = this.pathCopy(subjectIsMinor, registrantKind)

    if (this.hasInformationSharingHelpTarget) {
      this.informationSharingHelpTarget.textContent = content.informationSharingHelp
    }

    if (this.hasReviewIntroTarget) {
      this.reviewIntroTarget.textContent = content.reviewIntro
    }

    this.refreshSubmitLabel()
  }

  currentPathCopy() {
    return this.pathCopy(this.subjectAge() !== null && this.subjectAge() < 18, this.selectedRegistrantKind())
  }

  pathCopy(subjectIsMinor, registrantKind) {
    if (registrantKind === null) {
      return {
        submitLabel: "Complete registration",
        reviewIntro: "Check your answers before submitting.",
        informationSharingHelp: "Use this only for people who should receive scheduling, care, report, or safety information."
      }
    }

    if (subjectIsMinor && registrantKind === "self") {
      return {
        submitLabel: "Request guardian approval",
        reviewIntro: "Check your starter information before requesting guardian approval.",
        informationSharingHelp: "A parent or legal guardian can add information-sharing contacts during approval."
      }
    }

    if (subjectIsMinor) {
      return {
        submitLabel: "Complete registration",
        reviewIntro: "Check your answers before completing this minor registration.",
        informationSharingHelp: "As parent or legal guardian, use this only for people who should receive scheduling, care, report, or safety information."
      }
    }

    if (registrantKind === "someone_else") {
      return {
        submitLabel: "Request athlete approval",
        reviewIntro: "Check your answers before sending this to the adult athlete for approval.",
        informationSharingHelp: "The adult athlete must approve any requested information sharing before it is active."
      }
    }

    return {
      submitLabel: "Complete registration",
      reviewIntro: "Check your answers before completing registration.",
      informationSharingHelp: "Use this only for people who should receive scheduling, care, report, or safety information."
    }
  }

  personaPathContent(age, subjectIsMinor, registrantKind) {
    if (registrantKind === null) {
      return {
        key: "unknown",
        body: "The registration path depends on the athlete's age and whether the athlete or someone else is completing this form.",
        status: "Choose who is completing the form so we can direct you to the right path."
      }
    }

    if (subjectIsMinor && registrantKind === "someone_else") {
      return {
        key: "guardian-minor",
        body: "A parent or legal guardian can complete registration and choose any trusted contacts now.",
        status: "This registration can be completed today if guardian authority and signature are provided."
      }
    }

    if (subjectIsMinor) {
      return {
        key: "minor-self",
        body: "You can start the registration process, but a parent or legal guardian must approve before your registration and access are active.",
        status: "After submit, we will request guardian approval and keep your registration pending until it is completed."
      }
    }

    if (registrantKind === "someone_else") {
      return {
        key: "helper-adult",
        body: "You can enter information for the athlete, but the adult athlete must approve before registration and requested access are active.",
        status: "After submit, we will send the adult athlete an approval request."
      }
    }

    return {
      key: "adult-self",
      body: "The athlete can complete registration and choose any trusted contacts now.",
      status: "This registration can be completed today."
    }
  }

  syncGuardianFromRegistrant() {
    if (!this.hasGuardianSameAsRegistrantTarget || !this.guardianSameAsRegistrantTarget.checked) return
    if (!this.hasGuardianFirstNameTarget || !this.hasRegistrantFirstNameTarget) return

    this.guardianFirstNameTarget.value = this.registrantFirstNameTarget.value
    this.guardianLastNameTarget.value = this.registrantLastNameTarget.value
    this.guardianEmailTarget.value = this.registrantEmailTarget.value
    this.guardianPhoneTarget.value = this.registrantPhoneTarget.value

    if (["parent", "legal_guardian"].includes(this.registrantRelationshipTarget.value)) {
      this.guardianRelationshipTarget.value = this.registrantRelationshipTarget.value
    }

    if (!this.guardianSignatureWasEdited) {
      this.syncGuardianSignature()
    }

    this.refreshActionState()
  }

  toggleGuardianSameAsRegistrant() {
    this.guardianSameAsRegistrantWasChanged = true
    this.updateGuardianManualDetails()
    this.syncGuardianFromRegistrant()
    this.refreshActionState()
  }

  updateGuardianSameAsRegistrantState(shouldShow) {
    if (!this.hasGuardianSameAsRegistrantTarget) return

    const wrapper = this.guardianSameAsRegistrantTarget.closest(".attestation-check")
    if (wrapper) wrapper.hidden = !shouldShow
    this.guardianSameAsRegistrantTarget.disabled = !shouldShow

    if (!shouldShow) {
      this.guardianSameAsRegistrantTarget.checked = false
    } else if (!this.guardianSameAsRegistrantWasChanged) {
      this.guardianSameAsRegistrantTarget.checked = true
    }

    this.updateGuardianManualDetails()
  }

  updateGuardianManualDetails() {
    if (!this.hasGuardianManualDetailsTarget || !this.hasGuardianSameAsRegistrantTarget) return

    this.guardianManualDetailsTarget.hidden = this.guardianSameAsRegistrantTarget.checked
  }

  syncGuardianSignature() {
    if (!this.hasGuardianSignatureTarget || this.guardianSignatureWasEdited) return

    const name = [this.guardianFirstNameTarget?.value, this.guardianLastNameTarget?.value]
      .map((value) => value?.trim())
      .filter(Boolean)
      .join(" ")

    this.guardianSignatureTarget.value = name
  }

  markGuardianSignatureEdited() {
    this.guardianSignatureWasEdited = true
  }

  updateDependent(dependentId, clearWhenHidden) {
    const target = this.element.querySelector(`#${dependentId}`)
    const controls = Array.from(this.element.querySelectorAll(`[data-dependent-id="${dependentId}"]`))
    const selected = controls.find((control) => control.checked)
    const shouldShow = selected?.value === "true"

    target.hidden = !shouldShow
    target.querySelectorAll("input, select, textarea").forEach((control) => {
      control.disabled = !shouldShow

      if (!shouldShow && clearWhenHidden) {
        if (control.type === "checkbox" || control.type === "radio") {
          control.checked = false
        } else if (control.type !== "hidden") {
          control.value = ""
        }
      }
    })

    this.refreshConcussionDate()
    this.refreshActionState()
  }

  filterSports() {
    const query = this.sportsSearchTarget.value.trim().toLowerCase()
    let visibleCount = 0

    this.sportsOptionTargets.forEach((option) => {
      const matches = option.dataset.sportName.includes(query)
      option.hidden = !matches
      if (matches) visibleCount += 1
    })

    this.sportsEmptyTarget.hidden = visibleCount > 0
  }

  toggleAllInformation(event) {
    event.preventDefault()
    event.stopPropagation()

    const fields = this.allowedInformationFields(event.currentTarget)
    const checkedCount = fields.filter((field) => field.checked).length
    const shouldCheck = checkedCount === 0

    fields.forEach((field) => {
      field.checked = shouldCheck
    })

    this.refreshAllInformationState()
    this.refreshActionState()
  }

  refreshAllInformationState() {
    const allControl = this.hasAllInformationTarget ? this.allInformationTarget : this.element.querySelector("[data-registration-all-information]")
    if (!allControl || !this.hasAllowedCategoryTarget) return

    const selectable = this.allowedInformationFields(allControl)
    const checkedCount = selectable.filter((field) => field.checked).length

    allControl.checked = selectable.length > 0 && checkedCount === selectable.length
    allControl.indeterminate = checkedCount > 0 && checkedCount < selectable.length
  }

  allowedInformationFields(allControl) {
    const group = allControl.closest(".compact-options")
    if (!group) return this.allowedCategoryTargets

    const fields = Array.from(group.querySelectorAll("input[name='registration_intake[allowed_categories][]'], input[name$='[allowed_categories][]']"))
    return fields.length > 0 ? fields : this.allowedCategoryTargets
  }

  buildReview() {
    this.reviewTarget.replaceChildren()

    this.orderedStepIndexes().slice(0, -1).forEach((stepIndex) => {
      const step = this.stepTargets[stepIndex]
      const section = document.createElement("section")
      section.className = "registration-review__section"

      const heading = document.createElement("h3")
      heading.textContent = step.dataset.reviewSource
      section.appendChild(heading)

      const list = document.createElement("dl")
      step.querySelectorAll("[data-review-label]").forEach((field) => {
        if (this.hiddenWithinStep(field, step)) return

        const term = document.createElement("dt")
        term.textContent = field.dataset.reviewLabel

        const description = document.createElement("dd")
        description.textContent = this.reviewValue(field) || "Not provided"

        list.append(term, description)
      })

      section.appendChild(list)
      this.reviewTarget.appendChild(section)
    })
  }

  reviewValue(field) {
    const controls = Array.from(field.querySelectorAll("input, select, textarea"))
      .filter((control) => !control.disabled && control.type !== "hidden" && control.type !== "search")

    const checkableControls = controls.filter((control) =>
      control.type === "checkbox" || control.type === "radio"
    )
    const checkedControls = checkableControls.filter((control) => control.checked)

    if (checkedControls.length > 0) {
      return checkedControls.map((control) => this.readableControlValue(control, field)).join(", ")
    }

    if (checkableControls.length > 0) return ""

    const select = controls.find((control) => control.tagName === "SELECT")
    if (select) {
      return Array.from(select.selectedOptions).map((option) => option.text).filter(Boolean).join(", ")
    }

    return controls.find((control) => control.value.trim())?.value.trim() || ""
  }

  stepControls(index) {
    return Array.from(this.stepTargets[index].querySelectorAll("input, select, textarea"))
      .filter((control) => !control.disabled && control.type !== "hidden" && control.type !== "search")
  }

  stepIsValid(index) {
    return this.stepControls(index).every((control) => control.checkValidity())
  }

  orderedStepIndexes() {
    const approvalSteps = this.registrationCompletesAtSubmit() ? this.completionStepIndexes() : []

    if (this.minorCompletingOwnRegistration()) return [0, 3, 6]
    if (this.subjectAge() !== null && this.subjectAge() < 18) return [0, 3, 1, 2, ...approvalSteps, 6]

    return [0, 1, 2, 3, ...approvalSteps, 6]
  }

  completionStepIndexes() {
    const steps = []
    if (this.showEmptyMaterialStepsValue || this.hasOnboardingMaterialsValue) steps.push(4)
    if (this.showEmptyMaterialStepsValue || this.hasRequiredAttestationsValue) steps.push(5)
    return steps
  }

  registrationCompletesAtSubmit() {
    const age = this.subjectAge()
    const registrantKind = this.selectedRegistrantKind()

    if (age === null || registrantKind === null) return true

    const subjectIsMinor = age < 18
    return subjectIsMinor ? registrantKind === "someone_else" : registrantKind === "self"
  }

  minorCompletingOwnRegistration() {
    return this.subjectAge() !== null &&
      this.subjectAge() < 18 &&
      this.selectedRegistrantKind() === "self"
  }

  currentStepPosition(stepOrder) {
    const position = stepOrder.indexOf(this.currentStep)
    return position >= 0 ? position : 0
  }

  setButtonState(button, isDisabled) {
    button.disabled = isDisabled
    button.setAttribute("aria-disabled", isDisabled.toString())
  }

  readableControlValue(control, field) {
    if (control.dataset.reviewValue) return control.dataset.reviewValue

    if (control.id) {
      const explicitLabel = field.querySelector(`label[for="${control.id}"]`)
      if (explicitLabel) return explicitLabel.textContent.trim()
    }

    const wrappingLabel = control.closest("label")
    if (wrappingLabel && field.contains(wrappingLabel)) {
      const strongText = wrappingLabel.querySelector("strong")?.textContent.trim()
      return strongText || wrappingLabel.textContent.trim()
    }

    return control.value
  }

  hiddenWithinStep(field, step) {
    if (field.hidden) return true

    let element = field.parentElement

    while (element && element !== step) {
      if (element.hidden) return true
      element = element.parentElement
    }

    return false
  }

  togglePanel(panel, shouldShow) {
    if (!panel) return

    panel.hidden = !shouldShow
    panel.querySelectorAll("input, select, textarea").forEach((control) => {
      control.disabled = !shouldShow

      if (control.dataset.registrationRequiredWhenVisible !== undefined) {
        control.required = shouldShow
      }
    })
  }

  updateAthleteEmailRequirement() {
    if (!this.hasAthleteEmailTarget) return

    const age = this.subjectAge()
    const subjectIsAdult = age !== null && age >= 18

    this.athleteEmailTarget.disabled = false
    this.athleteEmailTarget.required = subjectIsAdult
    this.athleteEmailOptionalMarkTarget.hidden = subjectIsAdult
    this.athleteEmailHelpTarget.textContent = subjectIsAdult
      ? "Required for athletes 18 or older."
      : "Optional for minors. Guardian email is collected during approval."
  }

  updateContactChannelRequirements(addingAuthorizedContact) {
    if (this.hasAuthorizedContactPanelTarget) {
      this.authorizedContactPanelTargets.forEach((panel) => this.updateContactPanelChannelRequirements(panel))
      return
    }

    if (!this.hasContactPhoneTarget) return

    const selectedChannels = this.allowedChannelTargets
      .filter((control) => control.checked)
      .map((control) => control.value)
    const phoneRequired = addingAuthorizedContact && selectedChannels.some((channel) =>
      channel === "phone" || channel === "sms"
    )

    this.contactPhoneTarget.required = phoneRequired
    this.contactPhoneHelpTarget.textContent = phoneRequired
      ? "Required because phone or text communication is selected."
      : "Required if phone or text communication is selected."

    if (this.hasContactChannelNoticeTarget) {
      this.contactChannelNoticeTarget.hidden = !phoneRequired || this.contactPhoneTarget.value.trim().length > 0
    }

    this.refreshAllInformationState()
  }

  updateAuthorizedContactCards(informationSharingAvailable) {
    if (!this.hasAuthorizedContactItemTarget) return

    this.authorizedContactItemTargets.forEach((item) => {
      const toggle = item.querySelector("[data-registration-form-target~='authorizedContactTypeToggle']")
      const panel = item.querySelector("[data-registration-form-target~='authorizedContactPanel']")
      const otherField = item.querySelector("[data-registration-form-target~='contactTypeOtherField']")
      const shouldShow = informationSharingAvailable && toggle?.checked

      item.hidden = !informationSharingAvailable
      item.classList.toggle("is-open", shouldShow)

      if (panel) this.togglePanel(panel, shouldShow)
      if (otherField) this.togglePanel(otherField, shouldShow && item.dataset.contactType === "other")
      if (toggle) toggle.disabled = !informationSharingAvailable
    })
  }

  updateContactPanelChannelRequirements(panel) {
    const selectedChannels = Array.from(panel.querySelectorAll("input[name$='[allowed_channels][]']:checked"))
      .map((control) => control.value)
    const phoneRequired = selectedChannels.some((channel) => channel === "phone" || channel === "sms")
    const phoneField = panel.querySelector("[data-registration-form-target~='contactPhone']")
    const phoneHelp = panel.querySelector("[data-registration-form-target~='contactPhoneHelp']")
    const notice = panel.querySelector("[data-registration-form-target~='contactChannelNotice']")

    if (phoneField) phoneField.required = phoneRequired
    if (phoneHelp) {
      phoneHelp.textContent = phoneRequired
        ? "Required because phone or text communication is selected."
        : "Required if phone or text communication is selected."
    }
    if (notice && phoneField) {
      notice.hidden = !phoneRequired || phoneField.value.trim().length > 0
    }

    this.refreshAllInformationState()
  }

  selectedRegistrantKind() {
    return this.registrantKindTargets.find((control) => control.checked)?.value || null
  }

  selectedAuthorizedContactValue() {
    if (this.hasAuthorizedContactTypeToggleTarget && this.authorizedContactTypeToggleTargets.some((control) => control.checked)) return "1"
    if (this.hasAuthorizedContactToggleTarget) {
      return this.authorizedContactToggleTargets.find((control) => control.checked)?.value || this.authorizedContactToggleTarget.value || "0"
    }

    return "0"
  }

  subjectAge() {
    if (!this.hasDobTarget || !this.dobTarget.value) return null

    const dob = new Date(`${this.dobTarget.value}T00:00:00`)
    if (Number.isNaN(dob.getTime())) return null

    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const birthdayHasPassed =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate())

    if (!birthdayHasPassed) age -= 1

    return age
  }
}
